import axios from "axios";
import dayjs from "dayjs";
import { askReauthentication } from "../actions/Auth/actions";
import { scopes, tokenType } from "../authConfig";
import { msalInstance } from "../msal-instance";
import { tryCaptureOfflineRequest } from "../offline/apiClientOfflineCapture";
import { installETagInterceptor } from "../offline/etagInterceptor";
import { toast } from "../utils/toast";
import { IS_DEMO } from "./urls";

const TOAST_DEBOUNCE_SECONDS = 30;
let lastToastTime = dayjs().subtract(TOAST_DEBOUNCE_SECONDS + 1, "seconds"); // Initialize to ensure the first error can show

// Movement-integration tenants (e.g. Bakker SAP) suppress the local move and
// report it to the external system instead (header set by the backend). Debounce
// so a bulk move of many pallets surfaces a single informational toast.
const EXTERNAL_MOVE_TOAST_DEBOUNCE_SECONDS = 3;
let lastExternalMoveToastTime = dayjs().subtract(EXTERNAL_MOVE_TOAST_DEBOUNCE_SECONDS + 1, "seconds");

const accessTokenRequest = {
    scopes,
    redirectUri: "/blank.html",
};

export function processServerError(error) {
    if (Array.isArray(error)) {
        // * Handle the Pydantic RequestValidationError
        error.forEach((err) => processServerError(err));
    } else if (typeof error === "object" && error !== null) {
        // * Check if 'detail', 'message', or 'msg' properties are present
        if ("errors" in error && Array.isArray(error.errors)) {
            error.errors.forEach((err) => processServerError(err));
        } else if ("detail" in error) {
            toast.warning(error.detail);
        } else if ("message" in error) {
            toast.warning(error.message);
        } else if ("msg" in error) {
            toast.warning(error.msg);
        }
    } else if (typeof error === "string") {
        toast.warning(error);
    }
}

export interface APIClientOptions {
    onSuccess?: {
        showMessage: boolean;
        message: string;
    };
}

const apiClient = ({ onSuccess }: APIClientOptions = {}) => {
    const defaultOptions = {
        method: "get",
        headers: {
            "Content-Type": "application/json",
        },
    };

    // Create instance
    const instance = axios.create(defaultOptions);

    // Install the ETag-aware request/response interceptor BEFORE the auth
    // and error-handling interceptors below. Axios runs response
    // interceptors in installation order, so ETag's handler needs to fire
    // first to convert 304s / network errors on cached paths into
    // synthetic 200s before the generic error toaster sees them.
    // Gated at request time by enable_offline_checks; passes through
    // entirely for tenants without the flag. See offline/etagInterceptor.ts.
    installETagInterceptor(instance);

    // // Set the AUTH token for any request
    if (!IS_DEMO) {
        instance.interceptors.request.use(async (config) => {
            // const token = localStorage.getItem("avos_id_token");
            const token = await msalInstance.acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse) => accessTokenResponse)
                .catch((error) => {
                    // Acquire token silent failure
                    console.log("acquire token failed", error); // eslint-disable-line no-console
                    askReauthentication();
                });
            config.headers.Authorization = token ? `Bearer ${token[tokenType]}` : "";
            return config;
        });
    }

    // Use the same publicClientApplication instance provided to MsalProvider

    instance.interceptors.response.use(
        (data) => {
            if (onSuccess && onSuccess.showMessage) {
                toast.success(onSuccess.message);
            }
            // The move was handed off to the external system (SAP) instead of being
            // written locally; inform the user that the update will arrive back from
            // the external system shortly.
            if (data?.headers?.["x-external-movement-pending"] === "true"
                && dayjs().diff(lastExternalMoveToastTime, "seconds") > EXTERNAL_MOVE_TOAST_DEBOUNCE_SECONDS) {
                toast.info("Movement sent to the external system. The new location will appear once they confirm it.");
                lastExternalMoveToastTime = dayjs();
            }
            return Promise.resolve(data);
        },
        async (error) => {
            console.log("theError", error, error.response); // eslint-disable-line no-console
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.log("data", error.response.data); // eslint-disable-line no-console
                console.log("status", error.response.status); // eslint-disable-line no-console
                console.log("headers", error.response.headers); // eslint-disable-line no-console
                if (!error.config.no_toast && error.response.status === 400) {
                    processServerError(error.response.data);
                } else if (!error.config.no_toast && error.response.status === 401) {
                    askReauthentication();
                    processServerError(error.response.data);
                } else if (!error.config.no_toast && error.response.status === 422) {
                    processServerError(error.response.data);
                } else if (!error.config.no_toast && error.response.status === 403) {
                    processServerError(error.response.data);
                    // askReauthentication()
                } else if (!error.config.no_toast && error.response.status === 404 && error.response.data.type && error.response.data.type === "model_not_found") {
                    processServerError(error.response.data);
                } else if (!error.config.no_toast && error.response.status === 404) {
                    toast.error("Sorry, this api endpoint cannot be reached.");
                } else if (!error.config.no_toast && error.response.status === 500) {
                    processServerError(error.response.data);
                } else if (error.response.status === 0) {
                    // On appservice restart a status code of 0 is returned 9/10 times this due to a new release
                    // On auth/me this error is supressed

                    // NO_NETWORK ERRROR
                    // Probe the EFQ frontend rather than a third-party service.
                    // A cache-busting query prevents the service worker/browser
                    // cache from reporting success while the network is down.
                    axios.get(`${window.location.origin}/favicon.ico`, {
                        params: {
                            connectivity_probe: Date.now(),
                        },
                        headers: { "Cache-Control": "no-cache" },
                        timeout: 1000,
                    })
                        .then(() => {
                            if (!error.config.no_toast) {
                                toast.info(`We are restarting our service due to an update! Please retry in a minute.`, {
                                    pauseOnFocusLoss: false
                                });
                            }
                        })
                        .catch(() => {
                            console.log("User lost internet access"); // eslint-disable-line no-console
                            if (!error.config.no_toast) {
                                toast.error(<span>Your internet connection is <b> disrupted </b>. Please retry when you are back online.</span >, {
                                    pauseOnFocusLoss: false,
                                });
                            }

                        });
                } else if (!error.config.no_toast) {
                    processServerError(error.response.data);
                    // toast.warning(`${error.message}. View console for more info.`);
                }
                return Promise.reject(error);
            } if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                //
                // Issue 5.5: if there's an active offline session and the
                // request matches one of the recognised offline-capture
                // endpoints (PATCH /avocados/{id}/, PATCH /checks/{id}/,
                // POST /defects/register-bulk), persist the write to
                // IndexedDB and resolve with a synthetic 200 so the
                // redux-axios-middleware fires *.SUCCESS rather than
                // *.ERROR. The optimistic Redux update from *.LOADING
                // already applied the value, so the user sees no change.
                try {
                    const captured = await tryCaptureOfflineRequest(error);
                    if (captured) {
                        return captured;
                    }
                } catch (captureErr) {
                    // eslint-disable-next-line no-console
                    console.log("[offline] capture failed", captureErr);
                }
                console.log(error.request); // eslint-disable-line no-console
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log("Error", error.message); // eslint-disable-line no-console
            }

            // Something happened in setting up the request that triggered an Error
            console.log("Error", error); // eslint-disable-line no-console
            if (dayjs().diff(lastToastTime, "seconds") > TOAST_DEBOUNCE_SECONDS) { // More than 30 seconds since the last toast
                if (!error.config.no_toast) {
                    toast.warning(`${error.message}. View console for more info.`);
                    lastToastTime = dayjs(); // Update the time of the last shown toast
                }
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

export default apiClient();

export const createAPIClient = (options?: APIClientOptions) => apiClient(options);
